import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import { queryKeys } from '../lib/query'
import type { Meeting, MeetingStatus, PaginatedResponse } from '@1130-studio/shared'

// Types
interface MeetingsFilters {
  leadId?: string
  hostId?: string
  status?: MeetingStatus
  page?: number
  pageSize?: number
}

interface CreateMeetingInput {
  title: string
  leadId?: string
  scheduledAt: string
  durationMinutes?: number
  meetingUrl?: string
  description?: string
}

interface UpdateMeetingInput {
  title?: string
  description?: string | null
  scheduledAt?: string
  durationMinutes?: number
  meetingUrl?: string | null
  status?: MeetingStatus
  transcript?: string | null
  transcriptSummary?: string | null
  transcriptActionItems?: string[] | null
}

// Get meetings list
export function useMeetings(filters: MeetingsFilters = {}) {
  return useQuery({
    queryKey: queryKeys.meetings.list(filters),
    queryFn: () => {
      const params = new URLSearchParams()
      if (filters.leadId) params.set('leadId', filters.leadId)
      if (filters.hostId) params.set('hostId', filters.hostId)
      if (filters.status) params.set('status', filters.status)
      if (filters.page) params.set('page', String(filters.page))
      if (filters.pageSize) params.set('pageSize', String(filters.pageSize))

      const queryString = params.toString()
      return api.get<PaginatedResponse<Meeting>>(`/meetings${queryString ? `?${queryString}` : ''}`)
    },
  })
}

// Get single meeting
export function useMeeting(id: string) {
  return useQuery({
    queryKey: queryKeys.meetings.detail(id),
    queryFn: () => api.get<Meeting>(`/meetings/${id}`),
    enabled: !!id,
  })
}

// Create meeting
export function useCreateMeeting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateMeetingInput) => api.post<Meeting>('/meetings', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings.all })
    },
  })
}

// Update meeting
export function useUpdateMeeting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMeetingInput }) =>
      api.patch<Meeting>(`/meetings/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings.detail(variables.id) })
    },
  })
}

// Generate specification from transcript
export function useGenerateSpec() {
  return useMutation({
    mutationFn: ({ meetingId, model, additionalContext }: {
      meetingId: string
      model?: string
      additionalContext?: string
    }) => api.post<{
      specification: string
      tokensUsed: { input: number; output: number }
      cost: number
      model: string
    }>(`/meetings/${meetingId}/generate-spec`, { model, additionalContext }),
  })
}

// Generate estimation from specification
export function useGenerateEstimation() {
  return useMutation({
    mutationFn: ({ meetingId, specification, model }: {
      meetingId: string
      specification: string
      model?: string
    }) => api.post<{
      tasks: Array<{ name: string; hours: number; complexity: string; description: string }>
      totalHours: number
      estimatedCost: number
      tokensUsed: { input: number; output: number }
      cost: number
      model: string
    }>(`/meetings/${meetingId}/generate-estimation`, { specification, model }),
  })
}

// Delete meeting
export function useDeleteMeeting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => api.delete(`/meetings/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings.all })
    },
  })
}
